import { z } from 'zod';
import { WinChance } from './actions.js';
import { CATEGORY_SOURCES, EXCLUSION_REASONS } from '../expenses.js';
import { Cents, IsoDate, IsoDateTime, Uuid } from './common.js';
import { MonthKey } from './metrics.js';

/**
 * Données du lot B (CDC §3.7) : dépenses, devis et apporteurs, échéances, éléments non rattachés,
 * imports des Google Sheets.
 */

const Slug = z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Identifiant en minuscules et tirets attendu');

// ------------------------------------------------------------------ Canaux d'acquisition

/** Canaux d'acquisition connus (onglet Suivi CA, rubrique Acquisition ; maquette « D'où viennent les clients »). */
export const ACQUISITION_CHANNELS = [
  { key: 'partenaires-webdesign', label: 'Partenaires webdesign' },
  { key: 'recommandation', label: 'Recommandation' },
  { key: 'clients-existants', label: 'Clients existants' },
  { key: 'reseaux-pro', label: 'Réseaux pro' },
  { key: 'site-internet', label: 'Site internet' },
  { key: 'credit-agricole', label: 'Crédit Agricole' },
  { key: 'salon', label: 'Salon' },
  { key: 'autres', label: 'Autres' },
] as const;

/** Canal attribué à un devis signé avec un apporteur. */
export const PARTNER_CHANNEL_KEY = 'partenaires-webdesign';

// ------------------------------------------------------------------ Dépenses

export const ExpenseSource = z.enum(['axonaut_expense', 'bank_transaction', 'import']);
export type ExpenseSource = z.infer<typeof ExpenseSource>;

/** Origine du poste d'une ligne (lot P) : vous > règle > compte comptable > IA ; `import` pour le Sheet. */
export const ExpenseCategorySource = z.enum(CATEGORY_SOURCES);
export type ExpenseCategorySource = z.infer<typeof ExpenseCategorySource>;

/** « Pas une dépense » (lot P) : virement interne, TVA reversée, remboursement, autre. */
export const ExpenseExclusionReason = z.enum(EXCLUSION_REASONS);
export type ExpenseExclusionReason = z.infer<typeof ExpenseExclusionReason>;

export const Expense = z.object({
  id: Uuid,
  date: IsoDate,
  label: z.string(),
  /**
   * Montant de la dépense (positif ; négatif pour un avoir fournisseur). HT pour une dépense Axonaut ; montant
   * de l'opération (TVA inconnue) pour une ligne bancaire.
   */
  amountCents: Cents,
  categoryId: Uuid.nullable(),
  categoryName: z.string().nullable(),
  source: ExpenseSource,
  /** Compte comptable Axonaut, s'il est connu. */
  accountingCode: z.string().nullable(),
  /** Fournisseur (dépense Axonaut) ou contrepartie (ligne bancaire). */
  counterparty: z.string().nullable(),
  /** Montant TTC (ligne bancaire : la TVA n'est pas connue) ; sinon HT. L'écran affiche « TTC ». */
  isTaxIncluded: z.boolean(),
  /** Lot P : origine du poste (null = à catégoriser). */
  categorySource: ExpenseCategorySource.nullable(),
  /** Lot P : confiance de la prédiction de l'IA (0 à 1), null sinon. */
  categoryConfidence: z.number().nullable(),
  /** Lot P : « Pas une dépense » et son motif (null = dépense comptée). */
  excludedReason: ExpenseExclusionReason.nullable(),
  /** Lot P : `rule` = règle par défaut de la synchro, `user` = décision humaine. */
  excludedBy: z.enum(['rule', 'user']).nullable(),
  /** Lot P : dépense Axonaut que cette ligne bancaire double (elle n'est alors pas comptée). */
  duplicateOf: z.object({ id: Uuid, label: z.string(), date: IsoDate, amountCents: Cents }).nullable(),
  /**
   * Lot P : montant compté dans les totaux : 0 pour une ligne exclue ou un doublon, HT (÷ 1,2) pour une ligne
   * bancaire d'un poste à TVA récupérable, sinon `amountCents`.
   */
  countedCents: Cents,
  /** Lot P : le montant compté est le TTC ramené au HT. */
  vatConverted: z.boolean(),
});
export type Expense = z.infer<typeof Expense>;

/**
 * `GET /api/expenses?uncategorized=1&from=&to=` (tableau brut, du plus récent au plus ancien). Lot P :
 * `month` ou `year`, `categoryId` (`none` = sans poste), `review=1` (prédictions de l'IA peu sûres),
 * `status` (`counted` = comptées, `excluded` = pas des dépenses et doublons, `all` par défaut).
 */
export const ExpensesQuery = z.object({
  uncategorized: z.enum(['0', '1']).optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  month: MonthKey.optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
  categoryId: z.union([Uuid, z.literal('none')]).optional(),
  review: z.enum(['0', '1']).optional(),
  status: z.enum(['counted', 'excluded', 'all']).optional(),
});
export type ExpensesQuery = z.infer<typeof ExpensesQuery>;

/**
 * `PATCH /api/expenses/:id` : modification interne, sans plan (CDC §3.4).
 * - `categoryId` : poste choisi par la personne (`null` remet « à catégoriser ») ;
 * - `excludedReason` (lot P) : « Pas une dépense » avec son motif, `null` = la réintégrer ;
 * - `notDuplicate` (lot P) : « Ce n'est pas un doublon », la ligne bancaire est comptée et n'est plus rattachée.
 * Au moins un des trois.
 */
export const UpdateExpenseBody = z.object({
  categoryId: Uuid.nullable().optional(),
  excludedReason: ExpenseExclusionReason.nullable().optional(),
  notDuplicate: z.literal(true).optional(),
});
export type UpdateExpenseBody = z.infer<typeof UpdateExpenseBody>;

export const UpdateExpenseResult = z.object({
  expense: Expense,
  /**
   * Compte comptable Axonaut appris par le poste (si la dépense en porte un qu'aucun poste ne connaît
   * encore) : les prochaines dépenses de ce compte seront rangées automatiquement.
   */
  learnedAccountingCode: z.string().nullable(),
  /** Autres lignes à catégoriser rangées dans le même poste grâce à ce compte. */
  alsoCategorized: z.int().min(0),
  /**
   * Lot P : règle proposée après un changement de poste (« Appliquer aux opérations similaires »), avec le nombre
   * de lignes semblables qui n'ont pas déjà ce poste ni un poste choisi à la main. null si rien à proposer.
   */
  suggestedRule: z
    .object({
      field: z.enum(['label', 'counterparty']),
      pattern: z.string(),
      categoryId: Uuid,
      similarCount: z.int().min(0),
    })
    .nullable(),
});
export type UpdateExpenseResult = z.infer<typeof UpdateExpenseResult>;

// ------------------------------------------------------------------ Devis et apporteurs

export const QuoteStatus = z.enum(['pending', 'accepted', 'refused']);
export type QuoteStatus = z.infer<typeof QuoteStatus>;

export const QuoteLine = z.object({
  product: z.string().nullable(),
  label: z.string(),
  category: z.string(),
  amountCents: Cents,
});
export type QuoteLine = z.infer<typeof QuoteLine>;

export const Quote = z.object({
  id: Uuid,
  number: z.string().nullable(),
  title: z.string().nullable(),
  status: QuoteStatus,
  clientId: Uuid.nullable(),
  clientName: z.string().nullable(),
  amountCents: Cents,
  date: IsoDate.nullable(),
  sentAt: IsoDateTime.nullable(),
  answeredAt: IsoDateTime.nullable(),
  lines: z.array(QuoteLine),
  partnerId: Uuid.nullable(),
  acquisitionChannel: z.string().nullable(),
  /** Lot U : « Pas de projet pour ce devis » (date), absent ou null sinon. */
  projectIgnoredAt: IsoDateTime.nullable().optional(),
  /**
   * Lot AL : client partenaire (canal « Partenaire webdesign ») : ses frais d'intégration attendent un projet
   * partenaire. Renseigné par `GET /api/quotes?withoutProject=1` (simulateur), absent sinon.
   */
  partnerClient: z.boolean().optional(),
  /**
   * Lot AY : une ligne de maquette du devis est sous-traitée : « Maquette sous-traitée » est cochée d'office dans le
   * simulateur. Renseigné par `GET /api/quotes?withoutProject=1`, absent sinon.
   */
  mockupSubcontracted: z.boolean().optional(),
  /**
   * Lot BL : chance de signature (0 à 100 %, null = inconnue), qui l'a donnée et quand (carte « Chance de
   * signature » ou ligne du devis dans la simulation). Jamais écrite par la synchro Axonaut.
   */
  winChance: WinChance.optional(),
  winChanceBy: Uuid.nullable().optional(),
  winChanceAt: IsoDateTime.nullable().optional(),
  /** Lot BL : date « en ligne au plus tard » du client, reprise par « Préparer le projet ». */
  onlineBy: IsoDate.nullable().optional(),
  /** Lot BL : dernier R2 passé du client (Paris), rendu par `GET /api/quotes?status=pending` ; absent sinon. */
  lastR2On: IsoDate.nullable().optional(),
});
export type Quote = z.infer<typeof Quote>;

/**
 * `GET /api/quotes?attributed=0&status=&withoutProject=1` (tableau brut, du plus récent au plus ancien).
 * `attributed=0` : devis acceptés de l'année en cours (à Paris) sans apporteur ni canal (règle
 * `partner_missing`) ; `attributed=1` : devis avec un apporteur ou un canal ; `withoutProject=1` : devis
 * qu'aucun projet ne rattache encore (`projects.quote_id`), pour le simulateur « Un devis vient de signer »
 * (lot D1b).
 */
export const QuotesQuery = z.object({
  attributed: z.enum(['0', '1']).optional(),
  status: QuoteStatus.optional(),
  withoutProject: z.enum(['0', '1']).optional(),
  /**
   * Lot U : `1` = seulement les devis marqués « Pas de projet pour ce devis » (simulateur, « Devis ignorés ») ;
   * sans ce filtre, `withoutProject=1` les écarte.
   */
  projectIgnored: z.enum(['0', '1']).optional(),
});
export type QuotesQuery = z.infer<typeof QuotesQuery>;

/**
 * `PATCH /api/quotes/:id` : apporteur et/ou canal d'acquisition (interne, sans plan). Lot BL : chance de signature
 * (`winChance`, 0 à 100, null = inconnue) et date « en ligne au plus tard » (`onlineBy`, null = aucune).
 */
export const UpdateQuoteBody = z
  .object({
    partnerId: Uuid.nullable().optional(),
    acquisitionChannel: Slug.nullable().optional(),
    winChance: WinChance.optional(),
    onlineBy: IsoDate.nullable().optional(),
  })
  .refine(
    (b) =>
      b.partnerId !== undefined ||
      b.acquisitionChannel !== undefined ||
      b.winChance !== undefined ||
      b.onlineBy !== undefined,
    { message: 'Aucune modification demandée' },
  );
export type UpdateQuoteBody = z.infer<typeof UpdateQuoteBody>;

/** Lot W : autre nom d'un apporteur (nom tapé dans Axonaut rattaché à cet apporteur). */
export const PartnerAlias = z.object({ id: Uuid, name: z.string() });
export type PartnerAlias = z.infer<typeof PartnerAlias>;

/** Lot BN : `person` = apporteur à remercier ; `channel` = canal enregistré comme apporteur (« C'est un canal »). */
export const PartnerKind = z.enum(['person', 'channel']);
export type PartnerKind = z.infer<typeof PartnerKind>;

export const Partner = z.object({
  id: Uuid,
  name: z.string(),
  /** Lot BN : un apporteur `channel` sort du tableau et des totaux par apporteur. */
  kind: PartnerKind,
  /** Lot W : autres noms reconnus (champ Axonaut « Apporteur d'affaire »). */
  aliases: z.array(PartnerAlias),
  contact: z.string().nullable(),
  /** Solde TTC (comme dans le Sheet) ; négatif = en faveur du partenaire. */
  balanceTtcCents: Cents,
  /**
   * Lot BN : clients distincts ayant reçu un devis, depuis le début (règle d'attribution unique, mêmes chiffres que
   * `GET /api/partners/stats?period=all`).
   */
  quotesCount: z.int().min(0),
  /** Lot BN : nouveaux clients depuis le début. */
  clientsCount: z.int().min(0),
  /** Lot BN : CA signé de ces nouveaux clients depuis le début (part d'Evolusite, HT). */
  signedCents: Cents,
});
export type Partner = z.infer<typeof Partner>;

/**
 * `POST /api/partners` (membres connectés, interne, sans plan) : nouvel apporteur, solde à 0. Lot W : `alias` =
 * nom tapé dans Axonaut (« Créer l'apporteur » de la liste « Noms d'apporteurs inconnus »), enregistré comme autre nom s'il
 * diffère du nom choisi, pour être reconnu aux synchros suivantes.
 */
export const CreatePartnerBody = z.object({
  name: z.string().trim().min(1).max(200),
  contact: z.string().trim().max(200).nullable().optional(),
  alias: z.string().trim().min(1).max(200).optional(),
});
export type CreatePartnerBody = z.infer<typeof CreatePartnerBody>;

/** `PATCH /api/partners/:id` */
export const UpdatePartnerBody = z
  .object({
    name: z.string().trim().min(1).max(200).optional(),
    contact: z.string().trim().max(200).nullable().optional(),
    balanceTtcCents: Cents.optional(),
    /** Lot BN : « C'est un canal » (`channel`), réversible (`person`). */
    kind: PartnerKind.optional(),
  })
  .refine((b) => Object.values(b).some((v) => v !== undefined), { message: 'Aucune modification demandée' });
export type UpdatePartnerBody = z.infer<typeof UpdatePartnerBody>;

// ------------------------------------------------------------------ Échéances

export const Deadline = z.object({
  id: Uuid,
  clientId: Uuid.nullable(),
  clientName: z.string().nullable(),
  date: IsoDate,
  label: z.string(),
  createdBy: Uuid.nullable(),
  doneAt: IsoDateTime.nullable(),
  /** `manual` (saisie dans l'appli) ou `import` (onglet Relance du Sheet). */
  source: z.enum(['manual', 'import']),
});
export type Deadline = z.infer<typeof Deadline>;

/** `GET /api/deadlines?done=0` (tableau brut, par date croissante). */
export const DeadlinesQuery = z.object({ done: z.enum(['0', '1']).optional() });

/** `POST /api/deadlines` */
export const CreateDeadlineBody = z.object({
  clientId: Uuid.nullable().optional(),
  date: IsoDate,
  label: z.string().trim().min(1).max(500),
});
export type CreateDeadlineBody = z.infer<typeof CreateDeadlineBody>;

// ------------------------------------------------------------------ Éléments non rattachés (admin)

export const UnlinkedKind = z.enum(['site']);
export type UnlinkedKind = z.infer<typeof UnlinkedKind>;

/**
 * Lot R : raisons pour ignorer un site du back qui n'est pas un client (jamais facturé). `other` = autre
 * (précisée dans la note libre).
 */
export const SITE_IGNORE_REASONS = [
  'internal',
  'family',
  'redirection',
  'test',
  'not_billed',
  'other',
] as const;
export const SiteIgnoreReason = z.enum(SITE_IGNORE_REASONS);
export type SiteIgnoreReason = z.infer<typeof SiteIgnoreReason>;
export const SITE_IGNORE_REASON_LABELS: Record<SiteIgnoreReason, string> = {
  internal: 'Site interne',
  family: 'Famille',
  redirection: 'Redirection',
  test: 'Test',
  not_billed: 'Non facturé',
  other: 'Autre',
};

/** Site du back qu'aucun client ne porte (rattachement à un client). */
export const UnlinkedSiteItem = z.object({
  kind: z.literal('site'),
  id: Uuid,
  evolusiteSiteId: z.string(),
  name: z.string().nullable(),
  url: z.string().nullable(),
  /** `axonautId` envoyé par le back (null s'il est vide). */
  axonautId: z.string().nullable(),
  /** `no_axonaut_id` : le site du back ne porte pas d'identifiant Axonaut ; `unknown_axonaut_id` : aucun client Axonaut ne porte cet identifiant. */
  reason: z.enum(['no_axonaut_id', 'unknown_axonaut_id']),
  active: z.boolean().nullable(),
  subscription: z
    .object({ plan: z.string().nullable(), status: z.string(), monthlyAmountCents: Cents })
    .nullable(),
  /**
   * Lot R : « Probablement à ignorer » (nom finissant par « (Redirection) » ou contenant le mot « test »),
   * simple suggestion : rien n'est ignoré sans un clic. null : aucune suggestion.
   */
  ignoreSuggestion: SiteIgnoreReason.nullable(),
});
export type UnlinkedSiteItem = z.infer<typeof UnlinkedSiteItem>;

export const UnlinkedItem = z.discriminatedUnion('kind', [UnlinkedSiteItem]);
export type UnlinkedItem = z.infer<typeof UnlinkedItem>;

/**
 * Site du back ignoré (lot R, « ce n'est pas un client ») : masquage LOCAL au cockpit, rien n'est écrit dans
 * le back. Il n'est plus listé dans `items`, sort des règles et des métriques, et le reste aux synchros
 * suivantes (sauf si la synchro le rattache à un client par son identifiant Axonaut : il est alors rétabli).
 */
export const IgnoredSite = z.object({
  id: Uuid,
  evolusiteSiteId: z.string(),
  name: z.string().nullable(),
  url: z.string().nullable(),
  reason: SiteIgnoreReason.nullable(),
  note: z.string().nullable(),
  ignoredAt: IsoDateTime,
  /** Abonnement lu dans le back (un vrai abonnement Stripe actif compte toujours dans le revenu mensuel). */
  subscription: z
    .object({ plan: z.string().nullable(), status: z.string(), monthlyAmountCents: Cents })
    .nullable(),
});
export type IgnoredSite = z.infer<typeof IgnoredSite>;

/**
 * `POST /api/admin/unlinked/site/:id/ignore` : raison et note facultatives. Sur un site déjà ignoré, met à
 * jour la raison et la note (la date d'origine est gardée).
 */
export const IgnoreSiteBody = z
  .object({
    reason: SiteIgnoreReason.nullable().optional(),
    note: z.string().trim().max(200).nullable().optional(),
  })
  .strict();
export type IgnoreSiteBody = z.infer<typeof IgnoreSiteBody>;

/** `id` : UUID du site (`sites.id`). */
export const SiteParams = z.object({ id: z.string().min(1).max(100) });

/** `GET /api/admin/unlinked` : éléments non rattachés et candidats pour le rattachement manuel. */
export const UnlinkedList = z.object({
  /** Sites sans client et NON ignorés. */
  items: z.array(UnlinkedItem),
  /** Lot R : sites du back ignorés, les plus récemment ignorés d'abord (section « Sites ignorés »). */
  ignoredSites: z.array(IgnoredSite),
  /** Clients sans site du back, par nom. */
  clients: z.array(z.object({ id: Uuid, name: z.string(), axonautCompanyId: z.string().nullable() })),
});
export type UnlinkedList = z.infer<typeof UnlinkedList>;

/** `id` : UUID du site. */
export const UnlinkedParams = z.object({ kind: UnlinkedKind, id: z.string().min(1).max(100) });
/**
 * `PATCH /api/admin/unlinked/:kind/:id` : rattachement manuel d'un site à un client (interne, sans plan).
 */
export const LinkUnlinkedBody = z.object({ clientId: Uuid });
export type LinkUnlinkedBody = z.infer<typeof LinkUnlinkedBody>;

// ------------------------------------------------------------------ Imports des Google Sheets

/**
 * `POST /api/admin/import/sheet-finance` et `…/sheet-timeline` : soit un fichier .xlsx en multipart
 * (champ `file`, 10 Mo au plus), soit un corps JSON `{ spreadsheetUrl }` (lien ou identifiant du Google
 * Sheet, lu par l'API Google Sheets). `?dryRun=1` : aperçu sans écriture. `?overwriteObjectives=true` :
 * remplace les objectifs existants (sinon seuls les mois sans objectif sont remplis).
 */
export const ImportQuery = z.object({
  dryRun: z.enum(['0', '1', 'true', 'false']).optional(),
  overwriteObjectives: z.enum(['0', '1', 'true', 'false']).optional(),
});
export type ImportQuery = z.infer<typeof ImportQuery>;

export const ImportFromSheetBody = z.object({ spreadsheetUrl: z.string().trim().min(10).max(500) });
export type ImportFromSheetBody = z.infer<typeof ImportFromSheetBody>;

export const ImportIssue = z.object({
  level: z.enum(['info', 'warning']),
  code: z.string(),
  tab: z.string().nullable(),
  cell: z.string().nullable(),
  message: z.string(),
});
export type ImportIssue = z.infer<typeof ImportIssue>;

export const ImportUnmatchedClient = z.object({
  name: z.string(),
  evolusiteSiteId: z.int().nullable(),
  /** `no_site_id` : pas d'Id Site dans le Sheet ; `unknown_site` : site absent du back ; `site_unlinked` : site non rattaché à un client. */
  reason: z.enum(['no_site_id', 'unknown_site', 'site_unlinked']),
});
export type ImportUnmatchedClient = z.infer<typeof ImportUnmatchedClient>;

export const ImportResult = z.object({
  kind: z.enum(['finance', 'timeline']),
  dryRun: z.boolean(),
  /** Feuille lue : nom du fichier ou identifiant du Google Sheet. */
  source: z.string(),
  /** Éléments lus par entité (ex. `history_monthly`, `objectives`, `expense_categories`, `expenses`, `clients`, `deadlines`, `partners`). */
  counts: z.record(z.string(), z.int().min(0)),
  /** Lignes écrites par entité (absent en aperçu). */
  written: z.record(z.string(), z.int().min(0)).optional(),
  warnings: z.array(z.string()),
  issues: z.array(ImportIssue),
  /** Quelques lignes de chaque entité (5 au plus), telles qu'elles seront écrites. */
  samples: z.record(z.string(), z.array(z.unknown())),
  /** Clients du Sheet qui ne peuvent pas être enrichis (jamais de création de doublon). */
  unmatchedClients: z.array(ImportUnmatchedClient),
});
export type ImportResult = z.infer<typeof ImportResult>;
